// ヒストグラム (純ロジック)。RGBA から R / G / B / 輝度の 256 段カウントを作る。透明画素は除外。
export interface Histogram {
  r: Uint32Array
  g: Uint32Array
  b: Uint32Array
  lum: Uint32Array
  /** 集計した画素数 */
  count: number
  /** 各チャンネルの最大カウント (表示のスケール用) */
  max: { r: number; g: number; b: number; lum: number }
  mean: number
  median: number
}

export function computeHistogram(rgba: Uint8ClampedArray, stride = 1): Histogram {
  const r = new Uint32Array(256), g = new Uint32Array(256), b = new Uint32Array(256), lum = new Uint32Array(256)
  let count = 0
  let sum = 0
  const n = rgba.length / 4
  for (let i = 0; i < n; i += stride) {
    const o = i * 4
    if (rgba[o + 3] === 0) continue
    const R = rgba[o]!, G = rgba[o + 1]!, B = rgba[o + 2]!
    r[R]!++
    g[G]!++
    b[B]!++
    const L = Math.round(0.299 * R + 0.587 * G + 0.114 * B)
    lum[L]!++
    sum += L
    count++
  }
  const mx = (a: Uint32Array) => a.reduce((m, v) => (v > m ? v : m), 0)
  let median = 0
  let acc = 0
  for (let i = 0; i < 256; i++) {
    acc += lum[i]!
    if (acc >= count / 2) {
      median = i
      break
    }
  }
  return { r, g, b, lum, count, max: { r: mx(r), g: mx(g), b: mx(b), lum: mx(lum) }, mean: count ? sum / count : 0, median }
}

/** 自動トーン補正 / 自動コントラストのレベル値: 上下 0.1% を切り捨てた範囲を 0..255 に伸ばす */
export function autoLevelsFromHistogram(h: Histogram, mode: 'tone' | 'contrast', clip = 0.001): { rgb: { inBlack: number; inWhite: number; gamma: number; outBlack: number; outWhite: number }; r?: { inBlack: number; inWhite: number; gamma: number; outBlack: number; outWhite: number }; g?: { inBlack: number; inWhite: number; gamma: number; outBlack: number; outWhite: number }; b?: { inBlack: number; inWhite: number; gamma: number; outBlack: number; outWhite: number } } {
  const range = (arr: Uint32Array): [number, number] => {
    const total = arr.reduce((a, b) => a + b, 0)
    if (!total) return [0, 255]
    const lo = total * clip, hi = total * (1 - clip)
    let acc = 0, inBlack = 0, inWhite = 255
    for (let i = 0; i < 256; i++) { acc += arr[i]!; if (acc >= lo) { inBlack = i; break } }
    acc = 0
    for (let i = 255; i >= 0; i--) { acc += arr[i]!; if (acc >= total - hi) { inWhite = i; break } }
    if (inWhite <= inBlack) { inBlack = Math.max(0, inWhite - 1) }
    return [inBlack, inWhite]
  }
  const ch = (arr: Uint32Array) => { const [inBlack, inWhite] = range(arr); return { inBlack, inWhite, gamma: 1, outBlack: 0, outWhite: 255 } }
  if (mode === 'contrast') return { rgb: ch(h.lum) }
  return { rgb: { inBlack: 0, inWhite: 255, gamma: 1, outBlack: 0, outWhite: 255 }, r: ch(h.r), g: ch(h.g), b: ch(h.b) }
}
